import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth, TwoFactorRequiredError, TwoFactorSetupRequiredError } from '@repletoryreach/sdk-auth';
import { Stack, Title, Text, Button, Loader, Group } from '@repletoryreach/sdk-ui';
import { Alert } from '@repletoryreach/sdk-ui/data-display';
import { useT } from '../../../i18n/I18nProvider';
import { TwoFactorForm } from '../login/components/TwoFactorForm';

/**
 * Where a social provider's redirect lands.
 *
 * Identity has already done everything that matters: it spent the
 * authorisation code server-side, verified the provider's identity token
 * against that provider's own key set, and linked or created the account. What
 * reaches this page is a single-use handoff code, good for two minutes, which
 * is swapped here for the ordinary session. No access or refresh token has
 * been anywhere near the address bar.
 *
 * Or a refusal, as `?error=`. Each code gets a sentence saying what to do
 * next, because "STATE_INVALID" on a blank page is how somebody decides the
 * product is broken.
 */
export default function AuthCallbackPage() {
  const t = useT();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { completeSocialSignIn } = useAuth();

  const code = params.get('code');
  const errorCode = params.get('error');
  const provider = params.get('provider');
  const returnTo = params.get('return_to');

  const [failure, setFailure] = useState<string | null>(errorCode);
  // An account with an authenticator is asked for its code after Google too (9 October 2026).
  const [needsCode, setNeedsCode] = useState(false);
  // A new account goes to onboarding first, as a password sign-up does, where it is asked for the business's name
  // (owner, 9 October 2026); the organisation was named after the person.
  const landing = params.get('new_account') === '1' ? '/onboarding' : returnTo && returnTo.startsWith('/') ? returnTo : '/';
  // React runs effects twice in development's strict mode, and the handoff
  // code is single-use: the second call would always fail and paint an error
  // over a sign-in that had just succeeded.
  const spent = useRef(false);

  useEffect(() => {
    if (!code || errorCode || spent.current) return;
    spent.current = true;
    (async () => {
      try {
        await completeSocialSignIn(code, provider ?? undefined);
        navigate(landing, { replace: true });
      } catch (err) {
        if (err instanceof TwoFactorRequiredError) setNeedsCode(true);
        else if (err instanceof TwoFactorSetupRequiredError) setFailure('TWO_FACTOR_SETUP_REQUIRED');
        else setFailure('EXCHANGE_FAILED');
      }
    })();
  }, [code, errorCode, provider, landing, completeSocialSignIn, navigate]);

  if (needsCode) {
    return (
      <Stack gap="lg">
        <Title order={1}>{t('auth.callback.codeTitle')}</Title>
        <TwoFactorForm onDone={() => navigate(landing, { replace: true })} onBack={() => navigate('/auth/login')} />
      </Stack>
    );
  }

  if (!failure && code) {
    return (
      <Stack gap="md" align="center">
        <Loader />
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {t('auth.callback.signingIn')}
        </Text>
      </Stack>
    );
  }

  const message = failure ? messageFor(t, failure, provider) : t('auth.callback.error.SIGN_IN_FAILED');

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Title order={1}>{t('auth.callback.failedTitle')}</Title>
        <Text role="bodySmall" c="var(--rr-color-text-muted)">
          {t('auth.callback.failedLead')}
        </Text>
      </Stack>

      <Alert color="error" role="alert">{message}</Alert>

      {/* Buttons rather than links, because the sdk-ui Button renders a real
          <button> and does not take a `component` prop. */}
      <Group gap="sm">
        <Button onClick={() => navigate('/auth/login')}>{t('auth.callback.backToSignIn')}</Button>
        <Button variant="subtle" onClick={() => navigate('/auth/signup')}>{t('auth.login.createAccount')}</Button>
      </Group>
    </Stack>
  );
}

/**
 * Every refusal identity can redirect back with. The keys are the wire codes
 * from `identity.SocialSignInError`, so a code with no catalogued sentence
 * falls back to the general one rather than rendering the code itself.
 */
function messageFor(t: (key: string, vars?: Record<string, string | number>) => string, code: string, provider: string | null): string {
  const name = provider ? provider.charAt(0).toUpperCase() + provider.slice(1) : t('auth.social.provider');
  const known = [
    'STATE_INVALID', 'PROVIDER_REFUSED', 'PROVIDER_DISABLED', 'NO_CODE',
    'TOKEN_EXCHANGE_FAILED', 'NO_ID_TOKEN', 'ID_TOKEN_INVALID', 'NONCE_MISMATCH',
    'PROVIDER_UNREACHABLE', 'EMAIL_REQUIRED', 'EMAIL_NOT_VERIFIED', 'DOMAIN_NOT_ALLOWED',
    'LINK_REQUIRES_VERIFICATION', 'WRONG_DOMAIN', 'ACCOUNT_SUSPENDED',
    'PLATFORM_SIGNUP_BLOCKED', 'SIGNUP_FAILED', 'SIGN_IN_FAILED', 'EXCHANGE_FAILED', 'TWO_FACTOR_SETUP_REQUIRED'
  ];
  return known.includes(code)
    ? t(`auth.callback.error.${code}`, { provider: name })
    : t('auth.callback.error.SIGN_IN_FAILED', { provider: name });
}
